import { memo, useCallback } from 'react';
import {
  accountSearchColumns,
  boPhanSearchColumns,
  chiPhiKhongHopLeSearchColumns,
  dotThanhToanSearchColumns,
  hopDongSearchColumns,
  kheUocSearchColumns,
  lenhSanXuatSearchColumns,
  phiSearchColumns,
  QUERY_KEYS,
  thueSearchColumns,
  vatTu1SearchColumns,
  vatTuSearchColumns,
  vuViecSearchColumns,
  warehouseSearchColumns
} from '@/constants';
import {
  AccountModel,
  BoPhan,
  ChiPhiKhongHopLeData,
  DotThanhToan,
  HopDong,
  KheUoc,
  KhoHang,
  Phi,
  Tax,
  VatTu,
  VuViec
} from '@/types/schemas';
import {
  CellFieldV2 as CellField,
  ConversionQuantityFieldV2,
  DvtFieldV2,
  SearchFieldV2 as SearchField
} from '@/components/custom/erp';
import type { DataTableRenderCellParams as TableCellParams } from '@/components/custom/erp/data-table-v2';
import { Checkbox } from '@/components/ui/checkbox';
import { FormMode } from '@/types/form';

interface DetailCellProps {
  disabled: boolean;
  onCellValueChange: (rowUuid: string, field: string, newValue: BanHangValue) => void;
  row: BanHangRecord;
  rowUuid: string;
  value?: BanHangValue;
}

const MaVTCell = memo(function MaVTCell({
  disabled,
  onVatTuSelection,
  rowUuid,
  value,
  error
}: {
  disabled: boolean;
  onVatTuSelection?: (anchorRowUuid: string, rows: VatTu[]) => void | Promise<void>;
  rowUuid: string;
  value: VatTu[];
  error?: string;
}) {
  const handleSelection = useCallback(
    async (rows: VatTu[]) => {
      if (rows.length === 0 || !onVatTuSelection) {
        return;
      }

      await onVatTuSelection(rowUuid, rows);
    },
    [onVatTuSelection, rowUuid]
  );

  return (
    <SearchField<VatTu>
      multiple
      type='text'
      searchEndpoint={`/${QUERY_KEYS.VAT_TU}/`}
      searchColumns={vatTu1SearchColumns}
      columnDisplay='ma_vt'
      dialogTitle='Danh mục vật tư'
      value={value}
      onRowSelection={handleSelection}
      className='w-full'
      disabled={disabled}
      error={error}
      errorDisplay='tooltip'
    />
  );
});

const MaKhoCell = memo(function MaKhoCell({
  disabled,
  onCellValueChange,
  rowUuid,
  value,
  error
}: DetailCellProps & { error?: string }) {
  const handleSelection = useCallback(
    (row: KhoHang | null) => onCellValueChange(rowUuid, 'ma_kho_data', row),
    [onCellValueChange, rowUuid]
  );

  return (
    <SearchField<KhoHang>
      type='text'
      searchEndpoint={`/${QUERY_KEYS.KHO_HANG}/`}
      searchColumns={warehouseSearchColumns}
      columnDisplay='ma_kho'
      dialogTitle='Danh mục kho hàng'
      value={value}
      onRowSelection={handleSelection}
      className='w-full'
      disabled={disabled}
      error={error}
      errorDisplay='tooltip'
    />
  );
});

const SoLuongCell = memo(function SoLuongCell({
  disabled,
  onCellValueChange,
  row,
  error
}: DetailCellProps & { error?: string }) {
  return (
    <ConversionQuantityFieldV2
      row={row}
      onCellValueChange={onCellValueChange}
      disabled={disabled}
      showConversionInEdit
      commitOnBlur
      error={error}
      errorDisplay='tooltip'
    />
  );
});

const GiaNTCell = memo(function GiaNt2Cell({ disabled, onCellValueChange, rowUuid, value }: DetailCellProps) {
  const handleChange = useCallback(
    (newValue: BanHangValue) => onCellValueChange(rowUuid, 'gia_nt2', newValue),
    [onCellValueChange, rowUuid]
  );

  return (
    <CellField
      name='gia_nt2'
      type='number'
      value={value}
      onValueChange={handleChange}
      disabled={disabled}
      commitOnBlur
    />
  );
});

const TienNTCell = memo(function TienNt2Cell({ disabled, onCellValueChange, rowUuid, value }: DetailCellProps) {
  const handleChange = useCallback(
    (newValue: BanHangValue) => onCellValueChange(rowUuid, 'tien_nt2', newValue),
    [onCellValueChange, rowUuid]
  );

  return (
    <CellField
      name='tien_nt2'
      type='number'
      value={value}
      onValueChange={handleChange}
      disabled={disabled}
      commitOnBlur
    />
  );
});

export const getDetailTableColumns = (
  formMode: FormMode,
  ck_yn: boolean,
  onCellValueChange: (rowUuid: string, field: string, newValue: BanHangValue) => void,
  onVatTuSelection?: (anchorRowUuid: string, rows: VatTu[]) => void | Promise<void>,
  cellError?: (rowUuid: string, field: string) => string
) => buildDetailColumns(formMode, ck_yn, onCellValueChange, onVatTuSelection, cellError);

const buildDetailColumns = (
  formMode: FormMode,
  ck_yn: boolean,
  onCellValueChange: (rowUuid: string, field: string, newValue: BanHangValue) => void,
  onVatTuSelection?: (anchorRowUuid: string, rows: VatTu[]) => void | Promise<void>,
  cellError?: (rowUuid: string, field: string) => string
) => [
  {
    field: 'ma_vt',
    headerName: 'Mã sản phẩm',
    width: 200,
    renderCell: (params: TableCellParams) => (
      <MaVTCell
        disabled={formMode === 'view'}
        onVatTuSelection={onVatTuSelection}
        rowUuid={params.row.uuid}
        value={params.row.ma_vt_data ? [params.row.ma_vt_data] : []}
        error={cellError?.(params.row.uuid, 'ma_vt')}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_vt',
      resultField: 'ma_vt_data',
      detailEndpoint: QUERY_KEYS.VAT_TU
    }
  },
  {
    field: 'ten_vt',
    headerName: 'Tên sản phẩm',
    width: 200,
    renderCell: (params: TableCellParams) => (
      <CellField name={'ten_vt'} value={params.row.ma_vt_data?.ten_vt || ''} disabled={formMode === 'view'} />
    ),
    nonWritable: true
  },
  {
    field: 'dvt',
    headerName: 'Đvt',
    width: 120,
    renderCell: (params: TableCellParams) => (
      <DvtFieldV2
        row={params.row}
        onCellValueChange={onCellValueChange}
        disabled={formMode === 'view'}
        showConversionInEdit
      />
    ),
    nonWritable: true
  },
  {
    field: 'ma_kho',
    headerName: 'Mã kho',
    width: 150,
    renderCell: (params: TableCellParams) => (
      <MaKhoCell
        disabled={formMode === 'view'}
        onCellValueChange={onCellValueChange}
        row={params.row}
        rowUuid={params.row.uuid}
        value={params.row.ma_kho_data?.ma_kho}
        error={cellError?.(params.row.uuid, 'ma_kho')}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_kho',
      resultField: 'ma_kho_data',
      detailEndpoint: QUERY_KEYS.KHO_HANG
    }
  },
  {
    field: 'sl_ton',
    headerName: 'Tồn',
    width: 100,
    renderCell: (params: TableCellParams) => (
      <CellField
        name='sl_ton'
        type='number'
        value={params.row.sl_ton ?? 0}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'sl_ton', newValue)}
        disabled={true}
        commitOnBlur
      />
    ),
    nonWritable: true
  },
  {
    field: 'ct_km',
    headerName: 'Loại hàng',
    width: 150,
    renderCell: (params: TableCellParams) => (
      <CellField
        name='ct_km'
        type='select'
        options={[
          { value: '0', label: '0. Hàng bán' },
          { value: '1', label: '1. Hàng KM' }
        ]}
        value={params.row.ct_km === true ? '1' : params.row.ct_km || '0'}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'ct_km', newValue)}
        disabled={formMode === 'view'}
      />
    )
  },
  {
    field: 'so_luong',
    headerName: 'Số lượng',
    width: 150,
    renderCell: (params: TableCellParams) => (
      <SoLuongCell
        row={params.row}
        onCellValueChange={onCellValueChange}
        disabled={formMode === 'view'}
        rowUuid={params.row.uuid}
        error={cellError?.(params.row.uuid, 'so_luong')}
      />
    )
  },
  {
    field: 'gia_nt1',
    headerName: 'Giá chuẩn VND',
    width: 150,
    renderCell: (params: TableCellParams) => (
      <CellField
        name='gia_nt1'
        type='number'
        value={params.row.ma_vt_data?.gia_ton || params.row.gia_nt1}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'gia_nt1', newValue)}
        disabled={formMode === 'view'}
        commitOnBlur
      />
    ),
    nonWritable: true
  },
  {
    field: 'gia_nt2',
    headerName: 'Giá bán VND',
    width: 150,
    renderCell: (params: TableCellParams) => (
      <GiaNTCell
        value={params.row.gia_nt2}
        onCellValueChange={onCellValueChange}
        disabled={formMode === 'view'}
        row={params.row}
        rowUuid={params.row.uuid}
      />
    )
  },
  {
    field: 'tien_nt2',
    headerName: 'Thành tiền VND',
    width: 150,
    renderCell: (params: TableCellParams) => (
      <TienNTCell
        value={params.row.tien_nt2}
        onCellValueChange={onCellValueChange}
        disabled={formMode === 'view'}
        row={params.row}
        rowUuid={params.row.uuid}
      />
    )
  },
  ...(ck_yn
    ? [
        {
          field: 'tl_ck',
          headerName: 'Tỷ lệ chiết khấu (%)',
          width: 150,
          renderCell: (params: TableCellParams) => (
            <CellField
              name='tl_ck'
              type='number'
              value={params.row.tl_ck}
              onValueChange={newValue => onCellValueChange(params.row.uuid, 'tl_ck', newValue)}
              disabled={formMode === 'view'}
              commitOnBlur
            />
          )
        },
        {
          field: 'ck_nt',
          headerName: 'Chiết khấu VND',
          width: 150,
          renderCell: (params: TableCellParams) => (
            <CellField
              name='ck_nt'
              type='number'
              value={params.row.ck_nt}
              onValueChange={newValue => onCellValueChange(params.row.uuid, 'ck_nt', newValue)}
              disabled={formMode === 'view'}
              commitOnBlur
            />
          )
        }
      ]
    : []),
  {
    field: 'px_dd',
    headerName: 'Đích danh',
    width: 120,
    type: 'boolean',
    align: 'center',
    renderCell: (params: TableCellParams) => (
      <Checkbox
        checked={params.row.px_dd}
        onCheckedChange={newValue => onCellValueChange(params.row.uuid, 'px_dd', newValue)}
        disabled={formMode === 'view'}
      />
    ),
    nonWritable: true
  },
  {
    field: 'gia_nt',
    headerName: 'Giá tồn VND',
    width: 150,
    renderCell: (params: TableCellParams) => (
      <CellField
        name='gia_nt'
        type='number'
        value={params.row.gia_nt || (params.row.px_dd ? params.row.ma_vt_data?.gia_ton : undefined) || null}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'gia_nt', newValue)}
        disabled={!params.row.px_dd}
        commitOnBlur
      />
    )
  },
  {
    field: 'tien_nt',
    headerName: 'Tiền VND',
    width: 150,
    renderCell: (params: TableCellParams) => (
      <CellField
        name='tien_nt'
        type='number'
        value={
          params.row.tien_nt ||
          (params.row.px_dd && params.row.ma_vt_data?.gia_ton
            ? params.row.ma_vt_data.gia_ton * (params.row.so_luong || 1)
            : undefined) ||
          0
        }
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'tien_nt', newValue)}
        disabled={!params.row.px_dd}
        commitOnBlur
      />
    )
  },
  {
    field: 'ma_thue',
    headerName: 'Thuế suất',
    width: 250,
    renderCell: (params: TableCellParams) => (
      <SearchField<Tax>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.THUE}/`}
        searchColumns={thueSearchColumns}
        columnDisplay='ten_thue'
        dialogTitle='Danh mục thuế'
        value={params.row.ma_thue_data?.ten_thue}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_thue_data', row)}
        className='w-full'
        disabled={formMode === 'view'}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_thue',
      resultField: 'ma_thue_data',
      detailEndpoint: QUERY_KEYS.THUE
    }
  },
  {
    field: 'tk_thue_co',
    headerName: 'Tk thuế có',
    width: 120,
    renderCell: (params: TableCellParams) => (
      <SearchField<AccountModel>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
        searchColumns={accountSearchColumns}
        columnDisplay='code'
        dialogTitle='Danh mục tài khoản'
        value={params.row.tk_thue_co_data?.code}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'tk_thue_co_data', row)}
        className='w-full'
        disabled={formMode === 'view'}
      />
    ),
    pasteConfig: {
      lookupKey: 'tk_thue_co',
      resultField: 'tk_thue_co_data',
      detailEndpoint: QUERY_KEYS.TAI_KHOAN
    }
  },
  {
    field: 'thue_nt',
    headerName: 'Thuế VND',
    width: 120,
    renderCell: (params: TableCellParams) => (
      <CellField
        name='thue_nt'
        type='number'
        value={params.row.thue_nt}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'thue_nt', newValue)}
        disabled={formMode === 'view'}
        commitOnBlur
      />
    )
  },
  {
    field: 'tk_dt',
    headerName: 'Tk doanh thu',
    width: 150,
    renderCell: (params: TableCellParams) => (
      <SearchField<AccountModel>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
        searchColumns={accountSearchColumns}
        columnDisplay='code'
        dialogTitle='Danh mục tài khoản'
        value={params.row.tk_dt_data?.code || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'tk_dt_data', row)}
        className='w-full'
        disabled={formMode === 'view'}
      />
    ),
    pasteConfig: {
      lookupKey: 'tk_dt',
      resultField: 'tk_dt_data',
      detailEndpoint: QUERY_KEYS.TAI_KHOAN
    }
  },
  {
    field: 'tk_gv',
    headerName: 'Tk giá vốn',
    width: 150,
    renderCell: (params: TableCellParams) => (
      <SearchField<AccountModel>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
        searchColumns={accountSearchColumns}
        columnDisplay='code'
        dialogTitle='Danh mục tài khoản'
        value={params.row.tk_gv_data?.code || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'tk_gv_data', row)}
        className='w-full'
        disabled={formMode === 'view'}
      />
    ),
    pasteConfig: {
      lookupKey: 'tk_gv',
      resultField: 'tk_gv_data',
      detailEndpoint: QUERY_KEYS.TAI_KHOAN
    }
  },
  {
    field: 'tk_vt',
    headerName: 'Tk kho',
    width: 150,
    renderCell: (params: TableCellParams) => (
      <SearchField<AccountModel>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
        searchColumns={accountSearchColumns}
        columnDisplay='code'
        dialogTitle='Danh mục tài khoản'
        value={params.row.tk_vt_data?.code || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'tk_vt_data', row)}
        className='w-full'
        disabled={formMode === 'view'}
      />
    ),
    nonWritable: true
  },
  {
    field: 'tk_ck',
    headerName: 'Tk chiết khấu',
    width: 150,
    renderCell: (params: TableCellParams) => (
      <SearchField<AccountModel>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
        searchColumns={accountSearchColumns}
        columnDisplay='code'
        dialogTitle='Danh mục tài khoản'
        value={params.row.tk_ck_data?.code || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'tk_ck_data', row)}
        className='w-full'
        disabled={formMode === 'view'}
      />
    ),
    pasteConfig: {
      lookupKey: 'tk_ck',
      resultField: 'tk_ck_data',
      detailEndpoint: QUERY_KEYS.TAI_KHOAN
    }
  },
  {
    field: 'tk_km',
    headerName: 'Tk khuyến mãi',
    width: 150,
    renderCell: (params: TableCellParams) => (
      <SearchField<AccountModel>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
        searchColumns={accountSearchColumns}
        columnDisplay='code'
        dialogTitle='Danh mục tài khoản'
        value={params.row.tk_km_data?.code || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'tk_km_data', row)}
        className='w-full'
        disabled={formMode === 'view'}
      />
    ),
    pasteConfig: {
      lookupKey: 'tk_km',
      resultField: 'tk_km_data',
      detailEndpoint: QUERY_KEYS.TAI_KHOAN
    }
  },
  {
    field: 'ghi_chu',
    headerName: 'Ghi chú/Tên hàng hoá HĐĐT',
    width: 120,
    renderCell: (params: TableCellParams) => (
      <CellField
        name='ghi_chu'
        type='text'
        value={params.row.ghi_chu || ''}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'ghi_chu', newValue)}
        disabled={formMode === 'view'}
        commitOnBlur
      />
    )
  },
  {
    field: 'ma_bp',
    headerName: 'Bộ phận',
    width: 150,
    renderCell: (params: TableCellParams) => (
      <SearchField<BoPhan>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.BO_PHAN}/`}
        searchColumns={boPhanSearchColumns}
        columnDisplay='ma_bp'
        dialogTitle='Danh mục bộ phận'
        value={params.row.ma_bp_data?.ma_bp || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_bp_data', row)}
        error={cellError?.(params.row.uuid, 'ma_bp')}
        errorDisplay='tooltip'
        className='w-full'
        disabled={formMode === 'view'}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_bp',
      resultField: 'ma_bp_data',
      detailEndpoint: QUERY_KEYS.BO_PHAN
    }
  },
  {
    field: 'ma_vv',
    headerName: 'Vụ việc',
    width: 150,
    renderCell: (params: TableCellParams) => (
      <SearchField<VuViec>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.VU_VIEC}/`}
        searchColumns={vuViecSearchColumns}
        columnDisplay='ma_vu_viec'
        dialogTitle='Danh mục vụ việc'
        value={params.row.ma_vv_data?.ma_vu_viec || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_vv_data', row)}
        error={cellError?.(params.row.uuid, 'ma_vv')}
        errorDisplay='tooltip'
        className='w-full'
        disabled={formMode === 'view'}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_vv',
      resultField: 'ma_vv_data',
      detailEndpoint: QUERY_KEYS.VU_VIEC
    }
  },
  {
    field: 'ma_hd',
    headerName: 'Hợp đồng',
    width: 150,
    renderCell: (params: TableCellParams) => (
      <SearchField<HopDong>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.HOP_DONG}/`}
        searchColumns={hopDongSearchColumns}
        columnDisplay='ma_hd'
        dialogTitle='Danh mục hợp đồng'
        value={params.row.ma_hd_data?.ma_hd || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_hd_data', row)}
        error={cellError?.(params.row.uuid, 'ma_hd')}
        errorDisplay='tooltip'
        className='w-full'
        disabled={formMode === 'view'}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_hd',
      resultField: 'ma_hd_data',
      detailEndpoint: QUERY_KEYS.HOP_DONG
    }
  },
  {
    field: 'ma_dtt',
    headerName: 'Đợt thanh toán',
    width: 150,
    renderCell: (params: TableCellParams) => (
      <SearchField<DotThanhToan>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.DOT_THANH_TOAN}/`}
        searchColumns={dotThanhToanSearchColumns}
        columnDisplay='ma_dtt'
        dialogTitle='Danh mục đợt thanh toán'
        value={params.row.ma_dtt_data?.ma_dtt || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_dtt_data', row)}
        error={cellError?.(params.row.uuid, 'ma_dtt')}
        errorDisplay='tooltip'
        className='w-full'
        disabled={formMode === 'view'}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_dtt',
      resultField: 'ma_dtt_data',
      detailEndpoint: QUERY_KEYS.DOT_THANH_TOAN
    }
  },
  {
    field: 'ma_ku',
    headerName: 'Khế ước',
    width: 150,
    renderCell: (params: TableCellParams) => (
      <SearchField<KheUoc>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.KHE_UOC}/`}
        searchColumns={kheUocSearchColumns}
        columnDisplay='ma_ku'
        dialogTitle='Danh mục khế ước'
        value={params.row.ma_ku_data?.ma_ku || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_ku_data', row)}
        error={cellError?.(params.row.uuid, 'ma_ku')}
        errorDisplay='tooltip'
        className='w-full'
        disabled={formMode === 'view'}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_ku',
      resultField: 'ma_ku_data',
      detailEndpoint: QUERY_KEYS.KHE_UOC
    }
  },
  {
    field: 'ma_phi',
    headerName: 'Phí',
    width: 150,
    renderCell: (params: TableCellParams) => (
      <SearchField<Phi>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.PHI}/`}
        searchColumns={phiSearchColumns}
        columnDisplay='ma_phi'
        dialogTitle='Danh mục phí'
        value={params.row.ma_phi_data?.ma_phi || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_phi_data', row)}
        error={cellError?.(params.row.uuid, 'ma_phi')}
        errorDisplay='tooltip'
        className='w-full'
        disabled={formMode === 'view'}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_phi',
      resultField: 'ma_phi_data',
      detailEndpoint: QUERY_KEYS.PHI
    }
  },
  {
    field: 'ma_sp',
    headerName: 'Sản phẩm',
    width: 150,
    renderCell: (params: TableCellParams) => (
      <SearchField<VatTu>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.VAT_TU}/`}
        searchColumns={vatTuSearchColumns}
        columnDisplay='ma_vt'
        dialogTitle='Danh mục sản phẩm'
        value={params.row.ma_sp_data?.ma_vt || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_sp_data', row)}
        error={cellError?.(params.row.uuid, 'ma_sp')}
        errorDisplay='tooltip'
        className='w-full'
        disabled={formMode === 'view'}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_sp',
      resultField: 'ma_sp_data',
      detailEndpoint: QUERY_KEYS.VAT_TU
    }
  },
  {
    field: 'ma_lsx',
    headerName: 'Lệnh sản xuất',
    width: 150,
    renderCell: (params: TableCellParams) => (
      <SearchField<BanHangRecord>
        type='text'
        searchEndpoint={'/'}
        searchColumns={lenhSanXuatSearchColumns}
        columnDisplay='ma_lsx'
        dialogTitle='Danh mục lệnh sản xuất'
        value={params.row.ma_lsx_data?.ma_lsx || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_lsx_data', row)}
        error={cellError?.(params.row.uuid, 'ma_lsx')}
        errorDisplay='tooltip'
        className='w-full'
        disabled={formMode === 'view'}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_lsx',
      resultField: 'ma_lsx_data',
      detailEndpoint: QUERY_KEYS.LENH_SAN_XUAT
    }
  },
  {
    field: 'ma_cp0',
    headerName: 'C/p không h/lệ',
    width: 150,
    renderCell: (params: TableCellParams) => (
      <SearchField<ChiPhiKhongHopLeData>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.CHI_PHI_KHONG_HOP_LE}/`}
        searchColumns={chiPhiKhongHopLeSearchColumns}
        columnDisplay='ma_cp_khl'
        dialogTitle='Danh mục chi phí không hợp lệ'
        value={params.row.ma_cp0_data?.ma_cp || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_cp0_data', row)}
        error={cellError?.(params.row.uuid, 'ma_cp0')}
        errorDisplay='tooltip'
        className='w-full'
        disabled={formMode === 'view'}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_cp0',
      resultField: 'ma_cp0_data',
      detailEndpoint: QUERY_KEYS.CHI_PHI_KHONG_HOP_LE
    }
  },
  {
    field: 'sl_px',
    headerName: 'Sl đã xuất',
    width: 120,
    renderCell: (params: TableCellParams) => (
      <ConversionQuantityFieldV2 row={{ ...params.row, so_luong: params.row.sl_px }} disabled={true} />
    ),
    nonWritable: true
  },
  {
    field: 'so_ct_dh',
    headerName: 'Số đơn hàng',
    width: 180,
    renderCell: (params: TableCellParams) => params.row.id_dh?.so_ct,
    nonWritable: true
  },
  {
    field: 'line_dh',
    headerName: 'Dòng ĐH',
    width: 120,
    type: 'number',
    renderCell: (params: TableCellParams) => params.row.line_dh?.line,
    nonWritable: true
  },
  {
    field: 'so_ct_hd',
    headerName: 'Số hoá đơn',
    width: 120,
    renderCell: (params: TableCellParams) => params.row.so_ct_hd,
    nonWritable: true
  },
  {
    field: 'line_hd',
    headerName: 'Dòng HĐ',
    width: 120,
    renderCell: (params: TableCellParams) => params.row.line_hd,
    nonWritable: true
  }
];
